/**
 * Pull every page of a paginated list endpoint.
 *
 * Written on 2026-09-03 after the same fault turned up on four screens in one
 * day: a fetcher asks for page 1, the screen draws page 1, and no control
 * anywhere reaches page 2.
 *
 *   - Bunker Enquiries asked for `page=1&page_size=25` while the Prev/Next
 *     buttons sat commented out. 69 enquiries on the live system, 25 on the
 *     screen, 44 unreachable — the oldest 44, i.e. the desk's entire early
 *     history — and nothing said a row had been left out.
 *   - The admin audit trail loaded 50 of 576 rows, then ran its client-side
 *     action filters over those 50, so a filter could read "nothing has ever
 *     been changed" while hundreds of changes existed.
 *   - Leave audit: 50 of 416.
 *   - Deactivated users: the tab label showed the TRUE total above a list
 *     holding one page of it.
 *
 * Two rules, and either one alone lets the fault back:
 *
 *   1. **Ask for all of it.** That is this helper.
 *   2. **Say how much you are showing.** A list showing fewer rows than exist
 *      is otherwise indistinguishable from a list with fewer rows in it. That
 *      is why `total` and `truncated` come back — the caller is expected to
 *      render them.
 *
 * 2026-09-07 — the helper had the very fault it exists to prevent
 * -------------------------------------------------------------
 * It decided how many pages to pull from `total_pages` alone. A standard
 * Django REST Framework list body does not carry `total_pages`; it carries
 * `count` (how many rows exist) and `next` (a link, or null on the last page).
 * So for every DRF endpoint `total_pages` was undefined, `|| 1` filled the gap,
 * and the helper fetched page one and reported success. The function written to
 * stop silent truncation was silently truncating.
 *
 * The rule now: **completeness is read from what the server actually said**,
 * in this order, and where the server said nothing we stop at one page rather
 * than invent a page count.
 *
 *   1. `total_pages` — the HMS employees envelope states it outright.
 *   2. `has_next`    — DRF's `next` link, normalised to a yes/no.
 *   3. `total_count` — keep asking while we hold fewer rows than exist.
 *   4. nothing       — one page, and `total` still tells the caller the truth.
 *
 * `cap` is a runaway guard, not a product decision: it stops an unbounded loop
 * on a list that grows forever. When it bites, `truncated` is true and the
 * caller must say so on screen rather than present a slice as the whole book.
 *
 * @param fetchPage  (page) => Promise<{results, total_pages?, has_next?, total_count?}>
 *                   — use `normalizePage` below to build that from a raw body.
 * @param options    { cap } maximum pages to pull (default 40)
 * @returns { rows, total, truncated }
 */

/**
 * Does the server say there is another page after this one?
 *
 * Returns a definite true/false. "The server said nothing" is answered false —
 * asking blindly for page 2 of an endpoint that never paginated is how you get
 * an infinite loop, and the caller learns the truth from `total` either way.
 */
const serverSaysThereIsMore = (body, loadedSoFar, pageJustFetched) => {
  const totalPages = body?.total_pages;
  if (typeof totalPages === 'number' && Number.isFinite(totalPages)) {
    return pageJustFetched < totalPages;
  }
  // DRF states the next page as a LINK. `next: null` on the last page is the
  // server saying "that is all of it" — the only completeness signal a
  // standard DRF list body carries, and the one the old code ignored.
  if (typeof body?.has_next === 'boolean') {
    return body.has_next;
  }
  const totalCount = body?.total_count;
  if (typeof totalCount === 'number' && Number.isFinite(totalCount)) {
    return loadedSoFar < totalCount;
  }
  return false;
};

const fetchEveryPage = async (fetchPage, { cap = 40 } = {}) => {
  const first = await fetchPage(1);
  const rows = [...(first?.results || [])];

  let page = 1;
  let latest = first;
  let truncated = false;

  while (serverSaysThereIsMore(latest, rows.length, page)) {
    if (page >= cap) {
      // The runaway guard bit. This is a real truncation and the caller has
      // to say so on screen — never present this slice as the whole book.
      truncated = true;
      break;
    }
    page += 1;
    latest = await fetchPage(page);
    const batch = latest?.results || [];
    if (batch.length === 0) {
      // The server said there was more and then handed back nothing. We
      // cannot keep asking, and we must not call what we have complete.
      truncated = true;
      break;
    }
    rows.push(...batch);
  }

  return {
    rows,
    // Prefer the server's own count: it is the truth about the book, where
    // rows.length is only the truth about what we managed to load.
    total: typeof first?.total_count === 'number' ? first.total_count : rows.length,
    truncated,
  };
};

/**
 * Translate one page of a list endpoint into the shape `fetchEveryPage` wants.
 *
 * Added 2026-09-07, because the same three-line "unwrap the body" expression
 * was being retyped at every call site and each copy guessed differently —
 * `data.results || data.data || data` — which is how a screen ends up drawing
 * page one and believing it has the lot. Three envelopes are in use here:
 *
 *   - DRF's own:             { count, next, previous, results: [...] }
 *   - the HMS employees one: { success, data: [...], metadata: { total_pages,
 *                              total_elements } }
 *   - a bare array, from the endpoints that never paginated.
 *
 * Each key is only set when the body actually states it. `has_next` in
 * particular is left absent — not `false` — when there is no `next` key at
 * all, because "the server did not mention a next page" and "the server said
 * there is no next page" are different facts and only one of them is a promise
 * that the list is complete.
 */
export const normalizePage = (body) => {
  if (Array.isArray(body)) {
    // Not paginated at all — everything the endpoint has is right here.
    return { results: body, total_pages: 1, total_count: body.length, has_next: false };
  }

  const results = body?.results || body?.data || [];
  const page = { results: Array.isArray(results) ? results : [] };

  const totalPages = body?.metadata?.total_pages ?? body?.total_pages;
  if (typeof totalPages === 'number') page.total_pages = totalPages;

  const totalCount = body?.metadata?.total_elements ?? body?.total_count ?? body?.count;
  if (typeof totalCount === 'number') page.total_count = totalCount;

  if (body && typeof body === 'object' && 'next' in body) {
    page.has_next = Boolean(body.next);
  }

  return page;
};

export default fetchEveryPage;
